import _, { flatMap } from 'lodash';
import moment from 'moment';
import { KeyboardEvent, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import {
	CycleInstance,
	CycleStatus,
	CycleTemplate,
	MomentInstance,
	User,
	useCycleQuery,
	useRemoveCycleInstanceMutation,
	useUserCyclesQuery,
} from '@treams/treams-network';
import {
	AddIcon,
	ArchiveIcon,
	Author,
	Avatar,
	Button,
	ChevronDownIcon,
	ChevronRightIcon,
	DateIntervalNavigation,
	Empty,
	FlexGap,
	Heading3,
	ITimeMachine,
	IconButton,
	JunkIcon,
	MagicWandIcon,
	Paragraph,
	Pill,
	Popover,
	RepeatIcon,
	Skeleton,
	Table,
	tokens,
} from '@treams/treams-ui';

import {
	CycleMomentsPreview,
	useLocalStorageTimeMachine,
} from '../../packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';
import AssignCycle from './AssignCycle';

const CycleItem = styled.div`
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: none;
	}
`;

const CycleHeader = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.group}px;
	width: 100%;
	padding: ${tokens.padding.container}px ${tokens.padding.control}px;
	box-sizing: border-box;
	cursor: pointer;
	background: ${tokens.surface.white};
	color: ${tokens.text.primary};
`;

const CycleBody = styled(Box)`
	padding: 0 ${tokens.padding.control}px ${tokens.padding.container}px;
`;

type CyclePreviewProps = {
	id: string;
	timeMachineValue: ITimeMachine;
	setTimeMachine: (value: ITimeMachine) => void;
	bounds: [Date, Date];
};

function CyclePreview({ id, timeMachineValue, setTimeMachine, bounds }: CyclePreviewProps) {
	const { data } = useCycleQuery({ variables: { id } });

	if (!data) {
		return <CycleMomentsPreview />;
	}

	return (
		<CycleMomentsPreview
			dateRange={bounds}
			timeMachineValue={timeMachineValue}
			onChangeTimeMachine={(value) => setTimeMachine(value)}
			allowPagination={false}
			embedded
			cyclesLoading={false}
			momentsLoading={false}
			cycles={[data.cycleInstance as CycleInstance]}
			moments={data.cycleInstance.moments as MomentInstance[]}
			perspective="employee"
		/>
	);
}

function CycleAuthor(props: {
	cycle: {
		createdByUser: {
			id: string;
			firstName: string;
			lastName: string;
		};
		createdAt: Date;
	};
}) {
	const { t } = useTranslation();
	const { cycle } = props;

	const isCreatedBySystemUser = cycle.createdByUser.id === Avatar.SystemUserID;
	if (isCreatedBySystemUser) {
		return (
			<FlexGap alignItems="center" gap={tokens.gap.inline} py={`${tokens.padding.container}px`}>
				<MagicWandIcon size={20} />
				<Paragraph mb={0}>
					{t('settings.employees.cycles.user.automaticCycleAssignment')}:{' '}
					{moment(cycle.createdAt).format('LL')}
				</Paragraph>
			</FlexGap>
		);
	}

	return (
		<Box py={`${tokens.padding.container}px`}>
			<Author
				user={cycle.createdByUser}
				createdAt={cycle.createdAt}
				description={t('settings.employees.cycles.createdAt')}
			/>
		</Box>
	);
}

function CyclesEmpty(props: { onAssign: () => Promise<void>; user: User }) {
	const { t } = useTranslation('', { keyPrefix: 'settings.employees.cycles' });
	const [adding, setAdding] = useState(false);

	return (
		<Empty.Card
			icon={<RepeatIcon />}
			title={t('empty.title')}
			footer={
				<Popover.Target
					visible={adding}
					contents={
						<AssignCycle
							user={props.user}
							onCancel={() => setAdding(false)}
							onDismiss={() => setAdding(false)}
							onAssign={props.onAssign}
						/>
					}
				>
					<Button
						type="secondary"
						role="button"
						aria-label={t('assignButton')}
						icon={<AddIcon />}
						title={t('assignButton')}
						onClick={() => setAdding(true)}
					/>
				</Popover.Target>
			}
		/>
	);
}

function CyclesActions(props: { user: User; onAssign: () => Promise<void> }) {
	const { t } = useTranslation('', { keyPrefix: 'settings.employees.cycles' });
	const [adding, setAdding] = useState(false);

	return (
		<Popover.Target
			visible={adding}
			contents={
				<AssignCycle
					user={props.user}
					onCancel={() => setAdding(false)}
					onDismiss={() => setAdding(false)}
					onAssign={props.onAssign}
				/>
			}
		>
			<Button
				type="secondary"
				role="button"
				aria-label={t('assignButton')}
				icon={<AddIcon />}
				title={t('assignButton')}
				onClick={() => setAdding(true)}
			/>
		</Popover.Target>
	);
}

function statusTintForCycle(status: CycleStatus): string | undefined {
	const tints: Partial<Record<CycleStatus, string>> = {
		[CycleStatus.Active]: 'primary',
		[CycleStatus.Completed]: 'green',
		[CycleStatus.Pending]: 'blue',
		[CycleStatus.Stopped]: 'danger',
	};

	return tints[status];
}

function CycleRow(props: {
	cycle: {
		id: string;
		status: CycleStatus;
		cycleTemplate: CycleTemplate;
		startDate: Date;
		createdByUser: User;
		createdAt: Date;
	};
	onRemove: () => Promise<void>;
	timeMachineValue: ITimeMachine;
	setTimeMachine: (value: ITimeMachine) => void;
	bounds: [Date, Date];
}) {
	const { t } = useTranslation('', { keyPrefix: 'settings.employees.cycles' });
	const { localize } = useLocalize();
	const [remove] = useRemoveCycleInstanceMutation();
	const [expanded, setExpanded] = useState(props.cycle.status === CycleStatus.Active);
	const archived = props.cycle.status === CycleStatus.Archived;

	const toggle = () => setExpanded((value) => !value);

	const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			toggle();
		}
	};

	const onRemove = async (event: MouseEvent) => {
		event.stopPropagation();
		await remove({ variables: { input: { id: props.cycle.id } } });
		props.onRemove();
	};

	return (
		<CycleItem>
			<CycleHeader
				role="button"
				tabIndex={0}
				aria-expanded={expanded}
				onClick={toggle}
				onKeyDown={onHeaderKeyDown}
			>
				<FlexGap alignItems="center" gap={tokens.gap.group} minWidth={0}>
					<FlexGap alignItems="center" gap={tokens.gap.list} minWidth={0}>
						{expanded ? <ChevronDownIcon size={24} /> : <ChevronRightIcon size={24} />}
						<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0}>
							<Heading3 mb={0}>{localize(props.cycle.cycleTemplate.title).text}</Heading3>
							<Paragraph mb={0}>
								{t('startDate', { date: moment(props.cycle.startDate).format('LL') })}
							</Paragraph>
						</FlexGap>
					</FlexGap>
					<Pill tint={statusTintForCycle(props.cycle.status)}>
						{archived && <ArchiveIcon />}
						{t(`status.${props.cycle.status.toLowerCase()}`)}
					</Pill>
				</FlexGap>
				<IconButton type="destructive" icon={<JunkIcon />} onClick={onRemove} />
			</CycleHeader>
			{expanded && (
				<CycleBody>
					<CyclePreview
						id={props.cycle.id}
						timeMachineValue={props.timeMachineValue}
						setTimeMachine={props.setTimeMachine}
						bounds={props.bounds}
					/>
					<CycleAuthor cycle={props.cycle} />
				</CycleBody>
			)}
		</CycleItem>
	);
}

function CycleTable(props: {
	cycles: CycleInstance[];
	onRemove: () => Promise<void>;
	timeMachineValue: ITimeMachine;
	setTimeMachine: (value: ITimeMachine) => void;
	bounds: [Date, Date];
}) {
	return (
		<Table>
			{props.cycles.map((cycle) => (
				<CycleRow
					key={cycle.id}
					cycle={cycle}
					onRemove={props.onRemove}
					timeMachineValue={props.timeMachineValue}
					setTimeMachine={props.setTimeMachine}
					bounds={props.bounds}
				/>
			))}
		</Table>
	);
}

function CycleTableSkeleton() {
	return (
		<Table>
			<Skeleton.Repeat count={2}>
				<Table.Row tall>
					<Table.Cell.Skeleton grow />
					<Table.Cell.Skeleton width={80} />
					<Table.Cell.Skeleton width={32} />
				</Table.Row>
			</Skeleton.Repeat>
		</Table>
	);
}

export default function Cycles(props: { id: string }) {
	const { t } = useTranslation('', { keyPrefix: 'settings.employees.cycles' });

	const { timeMachine, setTimeMachine, bounds } = useLocalStorageTimeMachine();

	const query = useUserCyclesQuery({
		variables: {
			id: props.id,
			status: [],
		},
		fetchPolicy: 'network-only',
	});

	const cycles = _.sortBy(
		query.data?.user.cycles.edges.map((edge) => edge.node),
		'startDate',
	);
	const cyclesByStatus = _.groupBy(cycles, 'status');
	const currentStatusOrder = [
		CycleStatus.Active,
		CycleStatus.Pending,
		CycleStatus.Completed,
		CycleStatus.Stopped,
	];

	const currentCycles = flatMap(
		currentStatusOrder,
		(status) => (cyclesByStatus[String(status)] ?? []) as CycleInstance[],
	);
	const archivedCycles = (cyclesByStatus[String(CycleStatus.Archived)] ?? []) as CycleInstance[];

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
			{query.data ? (
				<Paragraph mb={0}>{t('description', { name: query.data.user.firstName })}</Paragraph>
			) : (
				<Paragraph.Skeleton lines={1} mb={0} />
			)}
			{query.data ? (
				query.data.user.cycles.edges.length === 0 ? (
					<CyclesEmpty
						user={query.data.user as User}
						onAssign={async () => {
							await query.refetch();
						}}
					/>
				) : (
					<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
						<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
							<FlexGap justifyContent="space-between" alignItems="center" width="100%">
								<CyclesActions
									user={query.data.user as User}
									onAssign={async () => {
										await query.refetch();
									}}
								/>
								<DateIntervalNavigation
									value={timeMachine}
									onChange={setTimeMachine}
									disableQuery
									allowFutureDates
								/>
							</FlexGap>
							{currentCycles.length > 0 && (
								<CycleTable
									cycles={currentCycles}
									onRemove={async () => {
										await query.refetch();
									}}
									timeMachineValue={timeMachine}
									setTimeMachine={setTimeMachine}
									bounds={bounds}
								/>
							)}
						</FlexGap>
						{archivedCycles.length > 0 && (
							<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
								<Heading3 mb={0}>{t('archived.title')}</Heading3>
								<CycleTable
									cycles={archivedCycles}
									onRemove={async () => {
										await query.refetch();
									}}
									timeMachineValue={timeMachine}
									setTimeMachine={setTimeMachine}
									bounds={bounds}
								/>
							</FlexGap>
						)}
					</FlexGap>
				)
			) : (
				<CycleTableSkeleton />
			)}
		</FlexGap>
	);
}
